import React, { useState } from "react";
import cn from "classnames";
import moment from "moment";
import { toast } from "react-toastify";
import style from "./ClientSuggestedPrices.module.scss";
import { Order } from "../../../../../../../services";
import { numberToFinanceFormat } from "../../../../../../../utils";
import { TenderDeadlineText } from "../../../../../../shared/TenderDeadlineText/TenderDeadlineText";
import { CURRENCIES } from "../../../../../../../mock";
import CommentIcon from "../../../../../../shared/icons/CommentIcon";
import AppTooltip from "../../../../../../shared/AppTooltip/AppTooltip";
import { useTranslation } from "react-i18next";
import ConfirmActionModal from "../../../../../order-info-expeditor/components/ConfirmActionModal/ConfirmActionModal";

const hasValue = (v) => v !== undefined && v !== null && v !== "";
const EMPTY_VALUE = "-";

const ClientSuggestedPrices = ({
  items = [],
  orderData = [],
  fetchOrderData,
  ordersStatus,
  onUpdateData,
  isPublic = false,
  user = "",
  tenderDateEnd,
  cargoDetails,
  isMarketSuggested = false,
  isDriverSuggested = false,
  title = "orderInfoClient.suggestedPrices.defaultTitle",
}) => {
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null, isPublic: null });

  const toggleComment = (priceId) => {
    setActiveCommentId((prevId) => (prevId === priceId ? null : priceId));
  };

  const openConfirmModal = (id, isPublicValue) => {
    setConfirmModal({ isOpen: true, id, isPublic: isPublicValue });
  };

  const handleConfirmAccept = () => {
    const { id } = confirmModal;
    setLoading(true);

    Order.acceptOrderBidById(id, { moderator: user?.name })
      .then((res) => res.data)
      .then(() => {
        toast.success(t("orderInfo.listSuggestedPrices.acceptSuccess"));
        setLoading(false);
        setConfirmModal({ isOpen: false, id: null, isPublic: null });
        onUpdateData?.();
        fetchOrderData?.();
      })
      .catch((error) => {
        console.error("Accept order bid error:", error);
        toast.error(t("orderInfo.listSuggestedPrices.acceptError"));
        setLoading(false);
        setConfirmModal({ isOpen: false, id: null, isPublic: null });
      });
  };

  const handleCancelConfirm = () => {
    setConfirmModal({ isOpen: false, id: null, isPublic: null });
  };

  const canAccept = ordersStatus === "awaitingoffer" || ordersStatus === "published";

  return (
    <div className={style.card}>
      {isMarketSuggested && orderData?.type === 0 && !isDriverSuggested && (
        <span className={style.note}>{t("orderInfoClient.suggestedPrices.marketplaceNote")}</span>
      )}

      <div className={style.topbar}>
        <span className={style.title}>{t(title)}</span>
        {tenderDateEnd && <span className={style.deadline}><TenderDeadlineText date={tenderDateEnd} /></span>}
      </div>

      <div className={style.head_row}>
        <span className={style.col_head}>{t("orderInfoClient.suggestedPrices.columnName")}</span>
        <span className={style.col_head}>{t("orderInfoClient.suggestedPrices.columnDate")}</span>
        <span className={style.col_head}>{t("orderInfoClient.suggestedPrices.columnSuggestedAmount")}</span>
        <span className={style.col_head}>{t("orderInfoClient.suggestedPrices.columnDeliveryTerm")}</span>
        <span className={style.col_head}/>
      </div>

      {items.length === 0 && <div className={style.empty}>{t("orderInfoClient.suggestedPrices.empty")}</div>}

      {items.map((item, index) => {
        const currencySymbol = CURRENCIES[item?.currency || "KZT"]?.symbol || "";
        const hasCommentDetails = item?.comments || hasValue(item?.weight) || hasValue(item?.volume);
        const bidName = item.expeditorName || item?.userName || EMPTY_VALUE;
        const bidNameTooltipId = `suggested-price-name-tooltip-${item.id || index}`;

        return (
          <React.Fragment key={item.id}>
            <div className={style.row}>
              <span
                className={cn(style.cell, style.cell_name)}
                data-label={t("orderInfoClient.suggestedPrices.columnName")}
                data-tooltip-id={bidNameTooltipId}
              >
                {bidName}
              </span>
              <span className={style.cell} data-label={t("orderInfoClient.suggestedPrices.columnDate")}>
                {moment(item?.createdAt).format("DD.MM.YYYY")} ({moment(item?.createdAt).format("HH:mm")})
              </span>
              <span
                className={style.cell_amount}
                data-label={t("orderInfoClient.suggestedPrices.columnSuggestedAmount")}
              >
                {numberToFinanceFormat(item.price)} {currencySymbol}
              </span>
              <span
                className={style.cell}
                data-label={t("orderInfoClient.suggestedPrices.columnDeliveryTerm")}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
              >
                {item?.plannedDays ? t("orderInfoClient.suggestedPrices.days", { count: item.plannedDays }) : EMPTY_VALUE}
                {hasCommentDetails && (
                  <button className={style.comment_btn} onClick={() => toggleComment(item.id)} title={t("orderInfoClient.suggestedPrices.commentTitle")}>
                    <CommentIcon type={3} fill="#212121" />
                  </button>
                )}
              </span>
              <div className={style.actions} data-label={t("common.actions")}>
                {canAccept ? (
                  <button
                    className={style.accept_btn}
                    onClick={() => openConfirmModal(item.id, isPublic)}
                    disabled={isLoading}
                  >
                    {t("orderInfoClient.suggestedPrices.accept")}
                  </button>
                ) : item?.isAccepted ? (
                  <span className={style.accepted}>{t("orderInfoClient.suggestedPrices.accepted")}</span>
                ) : null}
              </div>
            </div>
            <AppTooltip id={bidNameTooltipId} place="top" positionStrategy="fixed">
              {bidName}
            </AppTooltip>

            {activeCommentId === item.id && hasCommentDetails && (
              <div className={style.comment_box}>
                <div className={style.comment_box_head}>{t("orderInfoClient.suggestedPrices.commentTitle")}</div>
                <div className={style.comment_box_body}>
                  {item?.comments && <span>{item.comments}</span>}
                  {hasValue(item?.weight) && (
                    <span>
                      <b>{t("orderInfoClient.suggestedPrices.weight")}</b> {item.weight}
                    </span>
                  )}
                  {hasValue(item?.volume) && (
                    <span>
                      <b>{t("orderInfoClient.suggestedPrices.volume")}</b> {item.volume}
                    </span>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={t("orderInfo.listSuggestedPrices.confirmMessage")}
        onConfirm={handleConfirmAccept}
        onCancel={handleCancelConfirm}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ClientSuggestedPrices;
